import { telemetry } from "../telemetry/telemetry";
import { playNotify as playNotifyFallback } from "./audioEngine";

interface SpeakOptions {
  text: string;
  callId?: string;
  lang?: string;
  rate?: number;
  onDone?: () => void;
}

interface QueueItem extends SpeakOptions {
  enqueuedAt: number;
}

const ONSTART_TIMEOUT_MS = 1500;
const ONEND_TIMEOUT_MS = 8000;
const KEEPALIVE_INTERVAL_MS = 10 * 1000;
const MAX_QUEUE = 5;

const queue: QueueItem[] = [];
let speaking = false;
let keepAliveTimer: ReturnType<typeof setInterval> | null = null;

function hasSynth(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

function dispatch(): void {
  if (speaking) return;
  const next = queue.shift();
  if (!next) return;
  doSpeak(next);
}

function doSpeak(item: QueueItem): void {
  if (!hasSynth()) {
    telemetry("speech_error", { reason: "no_synth", callId: item.callId });
    playNotifyFallback({ callId: item.callId }).catch(() => {});
    item.onDone?.();
    dispatch();
    return;
  }

  if (typeof document !== "undefined" && document.hidden) {
    telemetry("speech_skipped_hidden", { callId: item.callId });
    playNotifyFallback({ callId: item.callId }).catch(() => {});
    item.onDone?.();
    dispatch();
    return;
  }

  speaking = true;

  try {
    window.speechSynthesis.cancel();
  } catch {
    // ignore — cancel pode lancar em alguns Chromes muito antigos.
  }

  const utter = new SpeechSynthesisUtterance(item.text);
  utter.lang = item.lang ?? "pt-BR";
  utter.rate = item.rate ?? 0.9;

  let started = false;
  let ended = false;
  let onstartTimer: ReturnType<typeof setTimeout> | null = null;
  let onendTimer: ReturnType<typeof setTimeout> | null = null;

  const finish = (): void => {
    if (ended) return;
    ended = true;
    if (onstartTimer) clearTimeout(onstartTimer);
    if (onendTimer) clearTimeout(onendTimer);
    speaking = false;
    item.onDone?.();
    dispatch();
  };

  utter.onstart = () => {
    started = true;
    if (onstartTimer) {
      clearTimeout(onstartTimer);
      onstartTimer = null;
    }
  };

  utter.onend = () => {
    if (started) {
      telemetry("speech_ok", { callId: item.callId });
    }
    finish();
  };

  utter.onerror = (e) => {
    telemetry("speech_error", {
      callId: item.callId,
      error: (e as any)?.error,
    });
    playNotifyFallback({ callId: item.callId }).catch(() => {});
    finish();
  };

  onstartTimer = setTimeout(() => {
    if (started || ended) return;
    telemetry("speech_no_onstart", { callId: item.callId });
    try {
      window.speechSynthesis.cancel();
    } catch {
      // ignore
    }
    playNotifyFallback({ callId: item.callId }).catch(() => {});
    finish();
  }, ONSTART_TIMEOUT_MS);

  onendTimer = setTimeout(() => {
    if (ended) return;
    if (started) {
      telemetry("speech_no_onend", { callId: item.callId });
      try {
        window.speechSynthesis.cancel();
      } catch {
        // ignore
      }
      finish();
    }
  }, ONEND_TIMEOUT_MS);

  try {
    window.speechSynthesis.speak(utter);
  } catch (err: any) {
    telemetry("speech_error", {
      callId: item.callId,
      reason: "speak_threw",
      message: err?.message,
    });
    playNotifyFallback({ callId: item.callId }).catch(() => {});
    finish();
  }
}

export function speak(opts: SpeakOptions): void {
  const item: QueueItem = { ...opts, enqueuedAt: Date.now() };
  if (queue.length >= MAX_QUEUE) {
    queue.splice(0, queue.length - (MAX_QUEUE - 1));
  }
  queue.push(item);
  dispatch();
}

export function warmup(): void {
  if (!hasSynth()) return;
  try {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    u.rate = 1;
    u.lang = "pt-BR";
    window.speechSynthesis.speak(u);
  } catch {
    // ignore — warmup eh best-effort.
  }
}

export function startKeepAlive(): void {
  if (keepAliveTimer) return;
  if (!hasSynth()) return;
  keepAliveTimer = setInterval(() => {
    try {
      const synth = window.speechSynthesis;
      if (synth.speaking || synth.pending) {
        synth.pause();
        synth.resume();
      } else {
        // Toque um utterance silencioso curto so para manter o engine vivo.
        // Apenas se a fila estiver vazia, evita interferir em chamadas reais.
        if (queue.length === 0 && !speaking) {
          const u = new SpeechSynthesisUtterance(" ");
          u.volume = 0;
          synth.speak(u);
        }
      }
    } catch {
      // ignore — keepalive nao pode lancar.
    }
  }, KEEPALIVE_INTERVAL_MS);
}

export function stopKeepAlive(): void {
  if (!keepAliveTimer) return;
  clearInterval(keepAliveTimer);
  keepAliveTimer = null;
}

export function cancelAll(): void {
  queue.length = 0;
  speaking = false;
  if (hasSynth()) {
    try {
      window.speechSynthesis.cancel();
    } catch {
      // ignore
    }
  }
}

// Indica se ha fala em andamento ou enfileirada. Usado pelo PanelContext
// para nao reloadar a pagina enquanto a voz esta tocando.
// - speaking: flag interna do nosso engine (queue dispatching).
// - synth.speaking / synth.pending: flags nativas do browser.
export function isSpeaking(): boolean {
  if (typeof window === "undefined") return false;
  if (!("speechSynthesis" in window)) return false;
  if (speaking) return true;
  try {
    const synth = window.speechSynthesis;
    return Boolean(synth.speaking || synth.pending);
  } catch {
    return false;
  }
}
